import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import { Box, IconButton, Tooltip, Divider, Typography } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  Image as ImageIcon,
  YouTube,
  FormatClear,
} from '@mui/icons-material';
import { noticiaService } from '../services/noticiaService';
import './RichTextEditor.css';

type RichTextEditorProps = {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  minHeight?: number;
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label = 'Conteúdo completo',
  minHeight = 280,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } }),
      Image.configure({ HTMLAttributes: { class: 'noticia-editor-img' } }),
      Youtube.configure({
        width: 640,
        height: 360,
        HTMLAttributes: { class: 'noticia-editor-youtube' },
      }),
    ],
    content: value || '<p></p>',
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || '<p></p>', { emitUpdate: false });
    }
  }, [value, editor]);

  const runImageUpload = async (file: File) => {
    if (!editor) return;
    try {
      const { url } = await noticiaService.uploadImagem(file);
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Não foi possível enviar a imagem.';
      window.alert(msg);
    }
  };

  const handleImageClick = () => fileInputRef.current?.click();

  const handleLink = () => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL do link:', previous || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleYoutube = () => {
    if (!editor) return;
    const url = window.prompt('Cole o link do vídeo no YouTube:');
    if (!url?.trim()) return;
    editor.commands.setYoutubeVideo({ src: url.trim() });
  };

  if (!editor) return null;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label} *
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Negrito, listas, links, fotos (upload) e vídeos do YouTube embutidos no texto.
      </Typography>
      <Box className="rich-text-editor" sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.25,
            px: 0.5,
            py: 0.5,
            bgcolor: 'grey.50',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tooltip title="Negrito">
            <IconButton
              size="small"
              color={editor.isActive('bold') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <FormatBold fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Itálico">
            <IconButton
              size="small"
              color={editor.isActive('italic') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <FormatItalic fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Lista com marcadores">
            <IconButton
              size="small"
              color={editor.isActive('bulletList') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <FormatListBulleted fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Lista numerada">
            <IconButton
              size="small"
              color={editor.isActive('orderedList') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <FormatListNumbered fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Inserir link">
            <IconButton
              size="small"
              color={editor.isActive('link') ? 'primary' : 'default'}
              onClick={handleLink}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Inserir foto">
            <IconButton size="small" onClick={handleImageClick}>
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Inserir vídeo do YouTube">
            <IconButton size="small" onClick={handleYoutube}>
              <YouTube fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Limpar formatação">
            <IconButton size="small" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>
              <FormatClear fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ minHeight, '& .ProseMirror': { minHeight: minHeight - 16 } }}>
          <EditorContent editor={editor} />
        </Box>
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) runImageUpload(file);
          e.target.value = '';
        }}
      />
    </Box>
  );
};

export default RichTextEditor;
